import { useState } from 'react';
import { DateCardField, ModalFooter, PaneHeader, saveBlockers, useToast, writeWithToast, useDirtyDraft } from '../common';
import { api } from '../../lib/api';
import type { Holiday, LeaveKind, Staff } from './types';

const KINDS: { value: LeaveKind; label: string }[] = [
  { value: 'annual', label: 'Annual' },
  { value: 'sick', label: 'Sick' },
  { value: 'unpaid', label: 'Unpaid' },
];

export interface LeaveFormProps {
  holiday: Holiday | null; // null = log new leave
  helper: Staff;
  today: string; // ISO
  onSaved: () => void;
  onClose: () => void;
}

// The leave pane (log + edit). Composes the shared PaneHeader / DateCardField /
// ModalFooter; only the kind segmented control + note are leave-specific.
// POST/PATCH/DELETE /holidays; the helper is fixed by the rail selection.
export function LeaveForm({ holiday, helper, today, onSaved, onClose }: LeaveFormProps) {
  const isNew = !holiday;
  const [kind, setKind] = useState<LeaveKind>(holiday?.kind ?? 'annual');
  const [from, setFrom] = useState(holiday?.fromDate ?? '');
  const [to, setTo] = useState(holiday?.toDate ?? '');
  const [note, setNote] = useState(holiday?.note ?? '');
  // The unsaved-changes guard's one line.
  useDirtyDraft('leave', 'this leave', { kind, from, to, note });
  const toast = useToast();

  // Why Save cannot be pressed, in the words to show — see ModalFooter's
  // `blockers`. TWO distinct refusals that one boolean used to merge into a
  // single silent grey button: nothing chosen, and a range the wrong way round.
  const blockers = saveBlockers(
    (!from || !to) && 'Choose the first and last day of the leave.',
    !!from && !!to && to < from && 'The last day cannot fall before the first.',
  );
  const canSave = blockers.length === 0;

  const save = () => {
    if (!canSave) return;
    const payload = { staffId: helper.id, fromDate: from, toDate: to, kind, note: note.trim() };
    // Close instantly; the save runs in the background.
    void writeWithToast(toast, {
      closeFirst: onClose,
      write: () => holiday ? api(`/holidays/${holiday.id}`, { method: 'PATCH', body: payload }) : api('/holidays', { method: 'POST', body: payload }),
      success: isNew ? 'Leave logged' : 'Leave updated',
      error: 'Could not save the leave.',
      onSaved,
    });
  };
  const doDelete = () => {
    if (!holiday) return;
    void writeWithToast(toast, {
      write: () => api(`/holidays/${holiday.id}`, { method: 'DELETE' }),
      success: 'Leave removed',
      error: 'Could not remove the leave.',
      onSaved,
    });
  };

  return (
    <div className={'pane anim-fade' + (isNew ? ' creating' : '')}>
      <div className="pane-scroll">
        <PaneHeader avatar={{ name: helper.name, id: helper.id }} title={isNew ? 'New Leave' : 'Edit Leave'} sub={`${helper.name} · ${helper.role}`} isNew={isNew} />

        <div className="scard">
          <div className="sh">Leave</div>
          <span className="ctl-label">Kind</span>
          <div className="useg" style={{ marginBottom: 14 }}>
            {KINDS.map((k) => (
              <button key={k.value} type="button" className={kind === k.value ? 'on' : ''} onClick={() => setKind(k.value)}>{k.label}</button>
            ))}
          </div>
          <span className="ctl-label">Dates</span>
          <DateCardField from={from} to={to} today={today} unit="day" onChange={(f, t) => { setFrom(f); setTo(t); }} />
        </div>

        <div className="scard">
          <div className="sh">Note <span className="field-hint">· optional</span></div>
          <div className="field" style={{ margin: 0 }}><textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. family trip · cover arranged with Charly" /></div>
        </div>
      </div>

      <ModalFooter
        onCancel={onClose}
        onSave={save}
        saveLabel={isNew ? 'Log leave' : 'Save changes'}
        blockers={blockers}
        danger={holiday ? { label: 'Remove leave', confirm: { message: 'Remove this leave?', confirmLabel: 'Remove leave', onConfirm: () => void doDelete() } } : null}
      />
    </div>
  );
}
